import { eq } from 'ember-truth-helpers'
import { tracked } from '@glimmer/tracking'
import Component from '@glimmer/component'
import type { TOC } from '@ember/component/template-only'
import { on } from '@ember/modifier'
import { t } from 'ember-intl'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'

export const AlertType = {
  Information: 'information',
  Warning: 'warning',
  Critical: 'critical',
  Success: 'success',
  NewFeature: 'new-feature',
}
export type AlertType = ValuesOf<typeof AlertType>

export interface InlineAlertSignature {
  Element: HTMLDivElement
  Args: {
    type: AlertType
    hideIcon?: boolean
    showClose?: boolean
  }
  Blocks: {
    default: []
  }
}

/**
 * @example
 *
 * <InlineAlert @type={{AlertType.Information}}>
 *   This is an informative example.
 * </InlineAlert>
 *
 * This component applies role="status", which is an approximation of how we use our alert component (more of a
 * non-urgent notification). If you need, role="alert" can be used for the alert to be treated as urgent, which will
 * interrupt screen readers.
 *
 * Typically, urgent "alert" would be a notification responding to user input; like form validation failed, or
 * "your changes will not be saved".
 *
 * https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/status_role
 * https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/alert_role
 */
export const InlineAlert: TOC<InlineAlertSignature> = <template>
  {{#if (eq @type AlertType.Information)}}
    <AlertContainer data-test-alert={{@type}} @showClose={{@showClose}} class="border-l-blue-325" ...attributes>
      <PhosphorIcon @icon="phosphor-info-fill" class="text-blue-325 h-6 w-6 shrink-0 {{if @hideIcon 'hidden'}}" />
      <div class="space-y-2">
        {{yield}}
      </div>
    </AlertContainer>
  {{else if (eq @type AlertType.Warning)}}
    <AlertContainer data-test-alert={{@type}} @showClose={{@showClose}} class="border-l-juicy-orange-400" ...attributes>
      <PhosphorIcon
        @icon="phosphor-warning-fill"
        class="text-juicy-orange-400 h-6 w-6 shrink-0 {{if @hideIcon 'hidden'}}"
      />
      <div class="space-y-2">
        {{yield}}
      </div>
    </AlertContainer>
  {{else if (eq @type AlertType.Critical)}}
    <AlertContainer data-test-alert={{@type}} @showClose={{@showClose}} class="border-l-red-300" ...attributes>
      <PhosphorIcon @icon="phosphor-warning-diamond-fill" class="h-6 w-6 shrink-0 text-red-300" />
      <div class="space-y-2">
        {{yield}}
      </div>
    </AlertContainer>
  {{else if (eq @type AlertType.Success)}}
    <AlertContainer data-test-alert={{@type}} @showClose={{@showClose}} class="border-l-green-300" ...attributes>
      <PhosphorIcon
        @icon="phosphor-check-circle-fill"
        class="h-6 w-6 shrink-0 text-green-300 {{if @hideIcon 'hidden'}}"
      />
      <div class="space-y-2">
        {{yield}}
      </div>
    </AlertContainer>
  {{else if (eq @type AlertType.NewFeature)}}
    <AlertContainer data-test-alert={{@type}} @showClose={{@showClose}} class="border-l-pink-300" ...attributes>
      <PhosphorIcon @icon="phosphor-sparkle-fill" class="h-6 w-6 shrink-0 text-pink-300 {{if @hideIcon 'hidden'}}" />
      <div class="space-y-2">
        {{yield}}
      </div>
    </AlertContainer>
  {{/if}}
</template>

interface AlertContainerSignature {
  Element: HTMLDivElement
  Args: {
    showClose?: boolean
  }
  Blocks: {
    default: []
  }
}

class AlertContainer extends Component<AlertContainerSignature> {
  @tracked dismissed = false

  onClose = () => {
    this.dismissed = true
  }

  <template>
    {{#unless this.dismissed}}
      <div
        class="border-y-neutral-75 border-r-neutral-75 flex gap-4 rounded-sm border-y border-r border-l-4 bg-white p-4 shadow-md print:hidden"
        role="status"
        {{! off, we don't intend for this to be updated with new information }}
        aria-live="off"
        ...attributes
      >
        {{yield}}
        {{#if @showClose}}
          <div class="ml-auto">
            <button type="button" aria-label={{t "close"}} {{on "click" this.onClose}} data-test-alert-close>
              <PhosphorIcon class="h-4 w-4" @icon="phosphor-x-bold" />
            </button>
          </div>
        {{/if}}
      </div>
    {{/unless}}
  </template>
}

interface AlertTitleSignature {
  Element: HTMLDivElement
  Blocks: {
    default: []
  }
}

export const InlineAlertTitle: TOC<AlertTitleSignature> = <template>
  <h5 class="text-sm font-medium text-neutral-400" ...attributes>{{yield}}</h5>
</template>

interface AlertSubtitleSignature {
  Element: HTMLDivElement
  Blocks: {
    default: []
  }
}

export const InlineAlertSubtitle: TOC<AlertSubtitleSignature> = <template>
  <div class="text-sm" ...attributes>{{yield}}</div>
</template>

interface AlertActionsContainerSignature {
  Element: HTMLDivElement
  Blocks: {
    default: []
  }
}

export const InlineAlertActionsContainer: TOC<AlertActionsContainerSignature> = <template>
  <div class="mt-4 space-x-4 px-4 py-2 text-sm font-medium" ...attributes>
    {{yield}}
  </div>
</template>

export default InlineAlert
